/**
 * DepartmentQuickCreateDialog — quick create for a People department.
 *
 * People departments are `user_groups` rows of type `department`, so a name is
 * the only field. It exists to feed DepartmentPicker's inline create: the
 * platform standard is that "+ Create [name]" in a combo always opens a dialog
 * with the typed name pre-filled and never POSTs straight from the popover
 * (STYLE_GUIDE § Inline Create From a Combo). Always skips navigation — the user
 * is mid-form on another entity.
 *
 * `POST /groups` is `require_admin`; the picker gates the create affordance on
 * the tenant-admin flag, so this dialog is only ever reachable by an admin.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { DialogForm } from '../../../shared/components/common';
import { useApiError } from '../../../shared/hooks/useApiError';
import { useCreateDepartment } from '../hooks/useGroups';

interface DepartmentQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  // No `skipNavigation`: the standard Quick Create Dialog prop is deliberately
  // omitted rather than missed. A department has no detail page to navigate to,
  // and this dialog only ever opens from the picker, so there is nothing to opt
  // out of.
  /** Called with the created department (id + name) before the dialog closes. */
  onCreated?: (department: { id: string; name: string }) => void;
  /** Pre-fill the name field (the picker's search text). */
  defaultName?: string;
}

interface QuickCreateFormData {
  name: string;
}

export function DepartmentQuickCreateDialog({
  open,
  onClose,
  onCreated,
  defaultName = '',
}: DepartmentQuickCreateDialogProps) {
  const { t } = useTranslation(['common']);
  const { createDepartment, isPending } = useCreateDepartment();
  const { handleMutationError } = useApiError();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (open) reset({ name: defaultName });
  }, [open, defaultName, reset]);

  const onSubmit = async (data: QuickCreateFormData) => {
    try {
      const created = await createDepartment(data.name.trim());
      onCreated?.({ id: created.data.id, name: created.data.name });
      onClose();
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('common:pickers.createDepartmentTitle')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting || isPending}
      maxWidth="sm"
    >
      <div className="space-y-2">
        <Label htmlFor="people-department-name" required>
          {t('common:pickers.departmentName')}
        </Label>
        <Controller
          name="name"
          control={control}
          rules={{ required: t('common:pickers.departmentNameRequired') }}
          render={({ field }) => (
            <Input
              {...field}
              id="people-department-name"
              autoFocus
              placeholder={t('common:pickers.departmentNamePlaceholder')}
              className={cn(errors.name && 'border-destructive')}
            />
          )}
        />
        {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
      </div>
    </DialogForm>
  );
}
